/**
 * @fileoverview Aba "Lembretes" do editor de triagem: o que a IA faz quando o
 * cliente para de responder. Até 5 lembretes, cada um com o tempo sem resposta
 * (contado do último pedido da IA), a mensagem e, na API oficial, o template
 * para quando a janela de 24 h estiver fechada; depois do último, seguir
 * esperando ou passar para uma pessoa; e o horário em que eles podem sair.
 */

import React from "react";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Tooltip from "@material-ui/core/Tooltip";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Plus, Trash2 } from "lucide-react";

import { i18n } from "../../../../translate/i18n";
import ModernSelect from "../../../../components/ModernSelect";
import useStyles from "./styles";
import SendWindowFields from "./SendWindowFields";
import { findTemplate, templateChoice, TemplateFields, templateProblem, useMetaTemplates } from "./metaTemplates";
import { DEFAULT_EXPIRE_AFTER_DAYS, FOLLOW_UP_MAX_STAGES, TIME_UNITS, newReminder, toMinutes } from "./formState";
import { translateWithLiterals } from "../../../../utils/translateWithLiterals";

const f = (key, params) => i18n.t(`triage.manager.editor.followUp.${key}`, params);

// Texto com nomes (fila, pessoa) por fora da interpolação do i18next: o nome
// fica como foi escrito, mesmo com "{{" ou "$t(".
const comNomes = (key, nomes) => translateWithLiterals(`triage.manager.editor.followUp.${key}`, nomes);

/** Para onde a conversa vai: o destino da passagem da aba Conclusão (fila e pessoa). */
const destinoDaPassagem = (handoff, queues, users) => {
  const pessoa = (users || []).find((u) => String(u.id) === String(handoff?.userId));
  const fila = (queues || []).find((q) => String(q.id) === String(handoff?.queueId));
  if (pessoa && fila) return comNomes("finalToUserQueue", { user: pessoa.name, queue: fila.name });
  if (pessoa) return comNomes("finalToUser", { user: pessoa.name });
  if (fila) return comNomes("finalToQueue", { queue: fila.name });
  return f("finalToKeep");
};

// Janela de 24 h da API oficial, em minutos: lembrete depois disso sem o
// cliente ter escrito só sai por template.
const JANELA_META = 24 * 60;

/** Quantidade + unidade (minutos, horas, dias). */
const TempoSemResposta = ({ amount, unit, onChange, label, classes }) => (
  <div className={classes.grid2}>
    <TextField
      className={classes.field}
      variant="outlined"
      size="small"
      type="number"
      label={label}
      InputLabelProps={{ shrink: true }}
      value={amount}
      onChange={(ev) => onChange({ amount: ev.target.value })}
      inputProps={{ min: 1, step: 1 }}
    />
    <ModernSelect
      className={classes.field}
      size="small"
      label={f("unit")}
      InputLabelProps={{ shrink: true }}
      value={unit}
      onChange={(ev) => onChange({ unit: ev.target.value })}
    >
      {Object.keys(TIME_UNITS).map((u) => (
        <MenuItem key={u} value={u}>
          {f(`units.${u}`)}
        </MenuItem>
      ))}
    </ModernSelect>
  </div>
);

/**
 * `onChange` recebe uma função (estado anterior → novo): dois templates que se
 * ajustam no mesmo instante (variáveis mudadas na Meta) não desfazem um ao outro.
 */
const FollowUpEditor = ({
  value,
  onChange,
  outreachConnection,
  replyMode,
  expireAfterDays,
  handoff,
  queues,
  users,
  hasRoutes = false,
  crmEnabled = false,
  onGoToCompletion,
}) => {
  const classes = useStyles();
  const set = (patch) => onChange((prev) => ({ ...prev, ...patch }));
  const isMeta = outreachConnection?.provider === "meta-api";
  const templates = useMetaTemplates(outreachConnection?.id, isMeta && value.enabled);

  const setStage = (uid, patch) =>
    onChange((prev) => ({ ...prev, stages: prev.stages.map((st) => (st.uid === uid ? { ...st, ...patch } : st)) }));

  const ligar = (enabled) =>
    onChange((prev) =>
      // Primeira vez: dois lembretes de exemplo (4 horas e 1 dia sem resposta).
      enabled && !prev.stages.length
        ? { ...prev, enabled, stages: [newReminder(4, "hours", f("defaultMessage1")), newReminder(1, "days", f("defaultMessage2"))] }
        : { ...prev, enabled }
    );

  const adicionar = () =>
    onChange((prev) => {
      const ultimo = prev.stages[prev.stages.length - 1];
      const minutos = ultimo ? (toMinutes(ultimo.amount, ultimo.unit) || 0) + TIME_UNITS.days : TIME_UNITS.days;
      const dias = Math.max(1, Math.round(minutos / TIME_UNITS.days));
      return { ...prev, stages: [...prev.stages, newReminder(dias, "days", "")] };
    });

  const remover = (uid) => onChange((prev) => ({ ...prev, stages: prev.stages.filter((o) => o.uid !== uid) }));

  // Vazio na aba Conclusão: o servidor usa o padrão (30 dias).
  const prazo = expireAfterDays === "" || expireAfterDays === null || expireAfterDays === undefined ? DEFAULT_EXPIRE_AFTER_DAYS : Number(expireAfterDays) || 0;

  return (
    <div className={classes.form}>
      <div className={classes.hint}>{f("intro")}</div>

      <FormControlLabel
        className={classes.check}
        control={<Switch color="primary" checked={value.enabled} onChange={(ev) => ligar(ev.target.checked)} />}
        label={f("enabled")}
      />

      {value.enabled && (
        <>
          {value.stages.map((st, i) => {
            const minutos = toMinutes(st.amount, st.unit);
            const escolhido = findTemplate(templates.list, st.template);
            const precisaTemplate = isMeta && minutos !== null && minutos >= JANELA_META && !st.template;
            return (
              <div key={st.uid} className={classes.item} data-reminder={i + 1}>
                <div className={classes.itemHead} style={{ cursor: "default" }}>
                  <span className={classes.itemOrder}>{i + 1}</span>
                  <div className={classes.itemTitle}>
                    <div className={classes.itemName}>{f("stageTitle", { n: i + 1 })}</div>
                  </div>
                  <Tooltip title={f("remove")}>
                    <button
                      type="button"
                      className={classes.iconBtn}
                      onClick={() => remover(st.uid)}
                      aria-label={f("removeStage", { n: i + 1 })}
                    >
                      <Trash2 />
                    </button>
                  </Tooltip>
                </div>
                <div className={classes.itemBody}>
                  <TempoSemResposta
                    classes={classes}
                    label={f("after")}
                    amount={st.amount}
                    unit={st.unit}
                    onChange={(patch) => setStage(st.uid, patch)}
                  />
                  <TextField
                    className={classes.field}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    label={f("message")}
                    placeholder={f("messagePlaceholder")}
                    helperText={replyMode === "configured" ? f("messageHintConfigured") : f("messageHint")}
                    InputLabelProps={{ shrink: true }}
                    value={st.message}
                    onChange={(ev) => setStage(st.uid, { message: ev.target.value })}
                    inputProps={{ maxLength: 1000 }}
                  />
                  {isMeta && (
                    <>
                      {templates.loading ? (
                        <div className={classes.hint} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <CircularProgress size={14} /> {i18n.t("triage.manager.editor.outreach.loadingTemplates")}
                        </div>
                      ) : templates.error ? (
                        <div className={classes.warnText}>{i18n.t("triage.manager.editor.outreach.templatesError")}</div>
                      ) : (
                        <ModernSelect
                          className={classes.field}
                          size="small"
                          fullWidth
                          label={f("template")}
                          helperText={f("templateHint")}
                          InputLabelProps={{ shrink: true }}
                          displayEmpty
                          value={escolhido ? `${escolhido.name}|${escolhido.language}` : ""}
                          onChange={(ev) => {
                            const tpl = templates.list.find((t) => `${t.name}|${t.language}` === ev.target.value);
                            setStage(st.uid, { template: tpl ? templateChoice(tpl, st.template) : null });
                          }}
                        >
                          <MenuItem value="">
                            <em>{f("templateNone")}</em>
                          </MenuItem>
                          {templates.list.map((tpl) => {
                            const problema = templateProblem(tpl);
                            return (
                              <MenuItem key={`${tpl.name}|${tpl.language}`} value={`${tpl.name}|${tpl.language}`} disabled={!!problema}>
                                {problema ? `${tpl.name} (${tpl.language}) · ${problema}` : `${tpl.name} (${tpl.language})`}
                              </MenuItem>
                            );
                          })}
                        </ModernSelect>
                      )}
                      {st.template && !escolhido && !templates.loading && !templates.error && (
                        <div className={classes.warnText}>
                          {i18n.t("triage.manager.editor.outreach.missingTemplate", { name: st.template.name })}
                        </div>
                      )}
                      <TemplateFields value={st.template} chosen={escolhido} onChange={(template) => setStage(st.uid, { template })} />
                      {precisaTemplate && <div className={classes.warnText}>{f("needsTemplate")}</div>}
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {value.stages.length < FOLLOW_UP_MAX_STAGES && (
            <div>
              <button type="button" className={classes.btnGhost} onClick={adicionar}>
                <Plus size={14} style={{ marginRight: 6 }} />
                {f("add")}
              </button>
            </div>
          )}

          <div>
            <div className={classes.label}>{f("final")}</div>
            <RadioGroup className={classes.radioGroup} value={value.finalAction} onChange={(ev) => set({ finalAction: ev.target.value })}>
              <FormControlLabel
                value="wait"
                control={<Radio size="small" color="primary" />}
                label={prazo > 0 ? f("finalWait", { days: prazo }) : f("finalWaitNever")}
              />
              <FormControlLabel value="handoff" control={<Radio size="small" color="primary" />} label={f("finalHandoff")} />
            </RadioGroup>
            {value.finalAction === "handoff" && (
              <div style={{ marginTop: 10 }}>
                <TempoSemResposta
                  classes={classes}
                  label={f("finalAfter")}
                  amount={value.finalAmount}
                  unit={value.finalUnit}
                  onChange={(patch) =>
                    set({
                      ...(patch.amount !== undefined && { finalAmount: patch.amount }),
                      ...(patch.unit !== undefined && { finalUnit: patch.unit }),
                    })
                  }
                />
                <div className={classes.hint} style={{ marginTop: 6 }}>
                  <strong>{destinoDaPassagem(handoff, queues, users)}</strong> {f("finalFrom")}{" "}
                  {onGoToCompletion && (
                    <button type="button" className={classes.link} onClick={onGoToCompletion}>
                      {f("finalChange")}
                    </button>
                  )}
                </div>
                {hasRoutes && <div className={classes.hint}>{f("finalRoutesFirst")}</div>}
                <div className={classes.hint}>{crmEnabled ? f("finalCrmNothingSent") : f("finalNothingSent")}</div>
              </div>
            )}
          </div>

          <SendWindowFields title={f("window")} hint={f("windowHint")} value={value} onChange={set} />

          <div className={classes.noteBox}>
            <div>{f("countNote")}</div>
            <div>{f("stopNote")}</div>
            {isMeta && <div>{f("metaNote")}</div>}
          </div>
        </>
      )}
    </div>
  );
};

export default FollowUpEditor;
